import React, { useMemo, useState, useEffect, useCallback } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import SectionHeader from "@cayuse/shared-react.components.section-header";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import IconButton from "@cayuse/shared-react.components.icon-button";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { getForm, resetForm } from "store/actions/form";
import { formSelectors, tenantSelectors } from "store/selectors";
import { RuleBuilderContextProvider } from "containers/Rules/context/RuleBuilderContext";
import { useDictionary, useFormRules, useComponentDidMount } from "hooks";
import { spUrl, getLastPublishedFormElements } from "utils";
import { COI_PRODUCT_KEY, FORMS_BASE_ROUTE } from "globals/constants";

import RulesListView from "containers/Rules/List";
import ConditionButton from "../List/ConditionButton/ConditionButton";

import styles from "./styles.css";
import { api } from "services";

const RulesMainView = () => {
    const { formId } = useParams();

    const form = useSelector(formSelectors.content, shallowEqual);
    const tenantId = useSelector(tenantSelectors.tenantId);

    const history = useHistory();
    const dictionary = useDictionary("ruleBuilder");
    const [rules, setRules] = useFormRules();
    const [newRuleOrder, setNewRuleOrder] = useState();
    const [editingCondition, setEditingCondition] = useState(false);
    const [formChanged, setFormChanged] = useState(false);
    const [isEditingAction, setEditingAction] = useState(false);
    const [isEditingOperand, setEditingOperand] = useState(false);
    const [ruleOrderIndexes, setRuleOrderIndexes] = useState(null);
    const [isDisclosureEnabled, setDisclosureEnabled] = useState(null);
    const [hasUnsavedOrderChanges, setHasUnsavedOrderChanges] = useState(false);
    const [lastPublishedFormElements, setLastPublishedFormElements] =
        useState();

    const componentDidMount = useComponentDidMount();

    useEffect(() => {
        /* reset form store on-mount and on-unmount */
        resetForm();

        return () => resetForm();
    }, []);

    useEffect(() => {
        resetForm();
        if (isNonEmptyString(formId) && componentDidMount) {
            const firstDashIndex = formId.indexOf("-");
            const trimmedId =
                firstDashIndex > -1
                    ? formId.substr(firstDashIndex + 1)
                    : formId;

            getForm(trimmedId);
        }
    }, [componentDidMount, formId]);

    

    useEffect(() => {
        const hasDisclosureEnabled = async () => {
            const response = await api.tenant.getTenantProducts(tenantId, 100);
            const COIProduct = response._embedded.tenant_products.find(
                product => product.active && product.name === COI_PRODUCT_KEY
            );
    
            setDisclosureEnabled(!!COIProduct);
        };
        hasDisclosureEnabled();
    }, [ tenantId ]);

    const onRuleChange = (updatedRule) => {
        const updatedRules = rules.map((rule) => {
            return rule.id === updatedRule.id ? updatedRule : rule;
        });

        setRules(updatedRules);
    };

    const retrieveLastPublishedFormElements = useCallback(async (modelKey) => {
        const result = await getLastPublishedFormElements(modelKey);
        setLastPublishedFormElements(result);
    }, []);

    useEffect(() => {
        if (form && !form.published) {
            retrieveLastPublishedFormElements(form.modelKey);
        }
    }, [form]);

    const buttons = useMemo(() => {
        return (
            <>
                <IconButton
                    icon="reply"
                    labelPosition="left"
                    secondary
                    onClick={() =>
                        history.push(spUrl(`${FORMS_BASE_ROUTE}/${formId}`))
                    }
                >
                    {dictionary.backButtonLabel || "Back"}
                </IconButton>
                <ConditionButton disabled={editingCondition || isEditingAction} />
            </>
        );
    }, [editingCondition, isEditingAction]);

    const breadcrumbs = [
        {
            text: dictionary.breadcrumbs?.root || "Forms",
            link: spUrl(FORMS_BASE_ROUTE),
        },
        {
            text: form.name || "Form Template",
            link: spUrl(`${FORMS_BASE_ROUTE}/${form.modelId}`),
        },
        {
            text: dictionary.breadcrumbs?.rules || "Rules",
        },
    ];

    return (
        <div data-component="RulesMainView">
            <VerticalSpacing>
                <RuleBuilderContextProvider
                    rules={rules}
                    setRules={setRules}
                    onRuleChange={onRuleChange}
                    formVersion={form.version}
                    formElements={
                        lastPublishedFormElements || form.formElements
                    }
                    formSections={form.formSections}
                    formType={form.name?.split(" ").shift().toUpperCase()}
                    formId={formId}
                    ruleOrderIndexes={ruleOrderIndexes}
                    setRuleOrderIndexes={setRuleOrderIndexes}
                    hasUnsavedOrderChanges={hasUnsavedOrderChanges}
                    setHasUnsavedOrderChanges={setHasUnsavedOrderChanges}
                    isDisclosureEnabled={isDisclosureEnabled}
                    editingCondition={editingCondition}
                    setEditingCondition={setEditingCondition}
                    isEditingAction={isEditingAction}
                    setEditingAction={setEditingAction}
                    isEditingOperand={isEditingOperand}
                    setEditingOperand={setEditingOperand}
                    formChanged={formChanged}
                    setFormChanged={setFormChanged}
                    newRuleOrder={newRuleOrder} 
                    setNewRuleOrder={setNewRuleOrder}
                >
                    <SectionHeader
                        header="Rule Builder"
                        buttons={[buttons]}
                        breadcrumbs={breadcrumbs}
                    />
                    <BasePadding>
                        <div className={styles.layout}>
                            <RulesListView key={"rules-list-view"} />
                        </div>
                    </BasePadding>
                </RuleBuilderContextProvider>
            </VerticalSpacing>
        </div>
    );
};

export default RulesMainView;
